import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  getApiErrorMessage,
  getMixRecipientConfigs,
  upsertMixRecipientConfig,
} from '../../../../api/mix-deliveriesApi';

const EMPTY_FORM = {
  recipientNumber: '',
  recipientName: '',
  sandwichingRequired: true,
  isActive: true,
};

export default function MixRecipientConfigsTable({
  refreshKey = 0,
  onDataChanged,
}) {
  const [rows, setRows] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecipientNumber, setEditingRecipientNumber] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [dialogError, setDialogError] = useState(null);

  const [notification, setNotification] = useState({
    open: false,
    message: '',
  });

  const loadConfigs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await getMixRecipientConfigs();

      setRows(Array.isArray(result) ? result : []);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs, refreshKey]);

  const filteredRows = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase('pl-PL');

    if (!normalizedSearch) {
      return rows;
    }

    return rows.filter((row) => {
      const recipientNumber = String(
        row.recipientNumber ?? '',
      ).toLocaleLowerCase('pl-PL');

      const recipientName = String(row.recipientName ?? '').toLocaleLowerCase(
        'pl-PL',
      );

      return (
        recipientNumber.includes(normalizedSearch) ||
        recipientName.includes(normalizedSearch)
      );
    });
  }, [rows, search]);

  const handleOpenCreate = () => {
    setEditingRecipientNumber(null);
    setForm(EMPTY_FORM);
    setDialogError(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (config) => {
    setEditingRecipientNumber(config.recipientNumber);

    setForm({
      recipientNumber: config.recipientNumber ?? '',
      recipientName: config.recipientName ?? '',
      sandwichingRequired: Boolean(config.sandwichingRequired),
      isActive: Boolean(config.isActive),
    });

    setDialogError(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (saving) {
      return;
    }

    setDialogOpen(false);
    setEditingRecipientNumber(null);
    setForm(EMPTY_FORM);
    setDialogError(null);
  };

  const handleSave = async () => {
    const recipientNumber = form.recipientNumber.trim();

    if (!recipientNumber) {
      setDialogError('Numer odbiorcy jest wymagany.');
      return;
    }

    setSaving(true);
    setDialogError(null);

    try {
      const result = await upsertMixRecipientConfig(recipientNumber, {
        recipientName: form.recipientName,
        sandwichingRequired: form.sandwichingRequired,
        isActive: form.isActive,
      });

      await loadConfigs();

      setDialogOpen(false);
      setEditingRecipientNumber(null);
      setForm(EMPTY_FORM);

      setNotification({
        open: true,
        message: buildSuccessMessage(result),
      });

      if (Number(result?.affectedDeliveriesCount ?? 0) > 0) {
        onDataChanged?.();
      }
    } catch (requestError) {
      setDialogError(getApiErrorMessage(requestError));
    } finally {
      setSaving(false);
    }
  };

  const handleApplySearch = () => {
    setSearch(searchInput.trim());
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
  };

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction={{
            xs: 'column',
            md: 'row',
          }}
          spacing={2}
          alignItems={{
            xs: 'stretch',
            md: 'center',
          }}
        >
          <TextField
            size="small"
            label="Szukaj odbiorcy"
            placeholder="Numer lub nazwa odbiorcy"
            value={searchInput}
            disabled={loading}
            onChange={(event) => setSearchInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                handleApplySearch();
              }
            }}
            sx={{
              minWidth: {
                xs: '100%',
                md: 320,
              },
            }}
          />

          <Box
            sx={{
              display: 'flex',
              gap: 1,
            }}
          >
            <Button
              variant="contained"
              disabled={loading}
              onClick={handleApplySearch}
            >
              Szukaj
            </Button>

            <Button
              variant="outlined"
              disabled={loading}
              onClick={handleClearSearch}
            >
              Wyczyść
            </Button>
          </Box>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            sx={{
              ml: {
                md: 'auto',
              },
            }}
          >
            Dodaj odbiorcę
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error">{error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Numer odbiorcy</TableCell>
              <TableCell>Nazwa odbiorcy</TableCell>
              <TableCell>Kanapkowanie</TableCell>
              <TableCell>Konfiguracja aktywna</TableCell>
              <TableCell>Ostatnia zmiana</TableCell>
              <TableCell align="right">Akcja</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            )}

            {!loading &&
              filteredRows.map((config) => (
                <TableRow
                  key={config.id ?? config.recipientNumber}
                  hover
                  sx={{
                    opacity: config.isActive ? 1 : 0.65,
                  }}
                >
                  <TableCell>
                    <Typography fontWeight={700}>
                      {config.recipientNumber}
                    </Typography>
                  </TableCell>

                  <TableCell>{config.recipientName ?? '—'}</TableCell>

                  <TableCell>
                    <Chip
                      size="small"
                      label={config.sandwichingRequired ? 'TAK' : 'NIE'}
                      color={config.sandwichingRequired ? 'warning' : 'default'}
                      variant={
                        config.sandwichingRequired ? 'filled' : 'outlined'
                      }
                      sx={{
                        fontWeight: config.sandwichingRequired ? 700 : 400,
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    <Chip
                      size="small"
                      label={config.isActive ? 'AKTYWNA' : 'NIEAKTYWNA'}
                      color={config.isActive ? 'success' : 'default'}
                      variant={config.isActive ? 'filled' : 'outlined'}
                    />
                  </TableCell>

                  <TableCell>{formatDateTime(config.updatedAt)}</TableCell>

                  <TableCell align="right">
                    <Tooltip title="Edytuj konfigurację">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEdit(config)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}

            {!loading && filteredRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                  Brak konfiguracji spełniających kryteria.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <RecipientConfigDialog
        open={dialogOpen}
        editing={Boolean(editingRecipientNumber)}
        form={form}
        saving={saving}
        error={dialogError}
        onChange={setForm}
        onClose={handleCloseDialog}
        onSave={handleSave}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() =>
          setNotification({
            open: false,
            message: '',
          })
        }
        message={notification.message}
      />
    </Stack>
  );
}

function RecipientConfigDialog({
  open,
  editing,
  form,
  saving,
  error,
  onChange,
  onClose,
  onSave,
}) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {editing
          ? 'Edytuj konfigurację odbiorcy'
          : 'Dodaj konfigurację odbiorcy'}
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="Numer odbiorcy"
            value={form.recipientNumber}
            disabled={saving || editing}
            required
            autoFocus={!editing}
            onChange={(event) =>
              onChange((current) => ({
                ...current,
                recipientNumber: event.target.value,
              }))
            }
            inputProps={{
              maxLength: 50,
            }}
          />

          <TextField
            label="Nazwa odbiorcy"
            value={form.recipientName}
            disabled={saving}
            onChange={(event) =>
              onChange((current) => ({
                ...current,
                recipientName: event.target.value,
              }))
            }
            inputProps={{
              maxLength: 255,
            }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={form.sandwichingRequired}
                disabled={saving}
                onChange={(event) =>
                  onChange((current) => ({
                    ...current,
                    sandwichingRequired: event.target.checked,
                  }))
                }
              />
            }
            label="Kanapkowanie wymagane"
          />

          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                disabled={saving}
                onChange={(event) =>
                  onChange((current) => ({
                    ...current,
                    isActive: event.target.checked,
                  }))
                }
              />
            }
            label="Konfiguracja aktywna"
          />

          {!form.isActive && (
            <Alert severity="info">
              Nieaktywna konfiguracja jest traktowana jak brak wymagania
              kanapkowania. Aktywne dostawy tego odbiorcy zostaną ustawione na
              „Kanapkowanie: NIE”.
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Anuluj
        </Button>

        <Button variant="contained" onClick={onSave} disabled={saving}>
          {saving ? 'Zapisywanie...' : 'Zapisz'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function buildSuccessMessage(result) {
  const affected = Number(result?.affectedDeliveriesCount ?? 0);
  const reviewRequired = Number(result?.reviewRequiredCount ?? 0);
  const resetToPrepare = Number(result?.resetToPrepareCount ?? 0);

  const parts = ['Konfiguracja odbiorcy została zapisana.'];

  if (affected > 0) {
    parts.push(`Zmienione aktywne dostawy: ${affected}.`);
  }

  if (reviewRequired > 0) {
    parts.push(`Do sprawdzenia po przygotowaniu: ${reviewRequired}.`);
  }

  if (resetToPrepare > 0) {
    parts.push(`Cofnięte do przygotowania: ${resetToPrepare}.`);
  }

  return parts.join(' ');
}

function formatDateTime(value) {
  if (!value) {
    return '—';
  }

  return new Intl.DateTimeFormat('pl-PL', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}
